import { useRedisWritesBlocked } from '@/apis/call';
import { updateList } from '@/apis/lists';
import { SUPPORTER_LIST_NAME_MAX_LEN } from '@/constants';
import { ApiBodyError, isDefinedError, UnexpectedStateError } from '@/errors';
import { CatCampaignRecord } from '@/types';
import { AgDialog, useSaladBar } from '@australiangreens/ag-internal-components';
import { useAuth0 } from '@auth0/auth0-react';
import CampaignAutocomplete from '@components/CampaignAutocomplete';
import { Box, TextField, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

const LIST_RENAME_MIN_LENGTH = 3;

const CHANGE_CAMPAIGN_WARNING_TEXT =
  'Changing the campaign will update all previously recorded interactions in this list. Are you sure you want to proceed?';

type EditListDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  listName: string;
  campaign?: CatCampaignRecord;
  listId: number;
};

const useEditList = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const { enqueueSuccessNotification } = useSaladBar();
  return useMutation({
    mutationFn: async ({
      listId,
      newName,
      newCampaign,
    }: {
      listId: number;
      newName: string;
      newCampaign: CatCampaignRecord;
    }) => {
      const token = await getAccessTokenSilently();
      return updateList(token, listId, { name: newName, campaignId: newCampaign?.id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lists'] });
      enqueueSuccessNotification('Changes have been saved');
    },
  });
};

function EditListDialogForm({
  listName,
  campaign,
  onClose,
  listId,
}: Omit<EditListDialogProps, 'isOpen'>) {
  const [newName, setNewName] = useState(listName);
  const [newCampaign, setNewCampaign] = useState<CatCampaignRecord | null>(campaign ?? null);
  const { enqueueErrorNotification } = useSaladBar();
  const writesBlocked = useRedisWritesBlocked();
  const { mutateAsync: editList, isError, error, reset } = useEditList();
  const [isSaving, setIsSaving] = useState(false);

  const handleEditListDialogSave = async () => {
    if (writesBlocked) return;
    try {
      setIsSaving(true);
      if (!newCampaign) {
        throw new UnexpectedStateError('Save button pressed despite newCampaign being null');
      }
      await editList({ listId, newName, newCampaign });
      onClose();
    } catch {
      enqueueErrorNotification('Changes could not be saved.');
    }
    setIsSaving(false);
  };

  const listNameErrorText = useMemo(() => {
    if (isError) {
      if (error instanceof ApiBodyError) {
        if (isDefinedError(error)) {
          const { message, name } = error.body.errors[0];
          if (name === 'UpdateSupporterListDuplicateNameError')
            return 'The list name has already been taken.';
          return message;
        }
        return 'There was an issue with the backend.';
      }
    }
    return '';
  }, [isError, error]);

  // As per AC3 of [LIST-731] the campaign is compulsary, even if editing an old
  // list which didn't have one before.
  const campaignErrorText = !newCampaign ? 'Select a campaign to save your changes' : undefined;

  const disableSaveButton =
    writesBlocked || newName.length < LIST_RENAME_MIN_LENGTH || isError || !newCampaign;

  // Show the changing campaign warning if there was a previously selected
  // campaign and the user selects a different one. We also don't want to show
  // it if the user has unselected a campaign, since they'll get a form error
  // instead.
  const showCampaignChangedWarning = campaign && newCampaign && newCampaign?.id !== campaign?.id;

  return (
    <AgDialog
      dialogTitle="Edit this list"
      isOpen={true}
      onClose={onClose}
      primaryButton={{
        text: 'Save',
        onClick: handleEditListDialogSave,
        disabled: disableSaveButton,
      }}
      data-testid="EditListDialog"
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem"
        }}>
        <TextField
          margin="dense"
          id="name"
          label={'List name'}
          type="text"
          fullWidth
          variant="standard"
          value={newName}
          onChange={(e) => {
            reset();
            setNewName(e.target.value);
          }}
          error={isError}
          helperText={listNameErrorText}
          slotProps={{
            htmlInput: {
              maxLength: SUPPORTER_LIST_NAME_MAX_LEN,
            },
          }}
        />

        <CampaignAutocomplete
          value={newCampaign}
          onChange={(newValue) => {
            reset();
            setNewCampaign(newValue);
          }}
          disabled={isSaving}
          sx={{ width: '100%' }}
          helperText={campaignErrorText}
          error={campaignErrorText !== undefined}
        />
        {showCampaignChangedWarning && (
          <Typography variant="body2" component="p" sx={{
            color: "info.main"
          }}>
            {CHANGE_CAMPAIGN_WARNING_TEXT}
          </Typography>
        )}
      </Box>
    </AgDialog>
  );
}

export default function EditListDialog({ isOpen, onClose, listName, campaign, listId }: EditListDialogProps) {
  if (!isOpen) {
    return null;
  }

  return (
    <EditListDialogForm
      key={listId}
      listName={listName}
      campaign={campaign}
      onClose={onClose}
      listId={listId}
    />
  );
}
